import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemButton,
  Snackbar,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Block,
  Bookmark,
  Campaign,
  BookmarkBorder,
  ChatBubbleOutline,
  Close,
  DeleteOutline,
  Edit,
  Favorite,
  FavoriteBorder,
  Flag,
  IosShare,
  Link as LinkIcon,
  MoreVert,
  PlaceOutlined,
  PlayArrowRounded,
  Repeat,
  Send,
  SmartDisplay,
  Verified,
  VisibilityOff,
  VolumeOffRounded,
  VolumeUpRounded,
} from "@mui/icons-material";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import { getSafeLink, normalizeReelEdit, ReelOverlays } from "./reelEdit";
import { isPublicPost } from "./postAudience";
import { recordAdEvent } from "./adEvents";

const BOTTOM_NAV_HEIGHT = 64;
const VISIBLE_THRESHOLD = 0.6;

const REELS_TEXT = {
  es: {
    title: "Reels",
    emptyTitle: "Todavía no hay reels",
    emptyHint: "Los videos que se publiquen van a aparecer acá.",
    edit: "Editar",
    delete: "Eliminar",
    promote: "Promocionar",
    sponsored: "Patrocinado",
    audienceFollowers: "Solo seguidores",
    audienceCloseFriends: "Mejores amigos",
    deleteTitle: "¿Eliminar este reel?",
    deleteWarning: "Esta acción no se puede deshacer.",
    deleted: "Reel eliminado",
    ownReelHint: "Estas opciones solo funcionan en reels de otras personas.",
    notInterested: "No me interesa",
    block: "Bloquear",
    report: "Reportar",
    close: "Cerrar",
    cancel: "Cancelar",
    save: "Guardar",
    editTitle: "Editar descripción",
    edited: "Descripción actualizada",
    hidden: "No volverás a ver este video",
    blockTitle: "¿Bloquear a {name}?",
    blockWarning: "No verás sus publicaciones y dejarás de seguirlo. Puedes desbloquearlo desde su perfil.",
    blocked: "Usuario bloqueado",
    reportTitle: "¿Por qué reportas este reel?",
    reported: "Reporte enviado. Gracias por avisarnos.",
    actionError: "No se pudo completar la acción. Intenta de nuevo.",
    reasonSpam: "Spam o estafa",
    reasonSexual: "Desnudez o contenido sexual",
    reasonViolence: "Violencia o contenido peligroso",
    reasonHate: "Odio o acoso",
    reasonFalse: "Información falsa",
    reasonChildSafety: "Seguridad infantil",
    reasonOther: "Otro motivo",
  },
  en: {
    title: "Reels",
    emptyTitle: "No reels yet",
    emptyHint: "Videos people post will show up here.",
    edit: "Edit",
    delete: "Delete",
    promote: "Promote",
    sponsored: "Sponsored",
    audienceFollowers: "Followers only",
    audienceCloseFriends: "Close friends",
    deleteTitle: "Delete this reel?",
    deleteWarning: "This action cannot be undone.",
    deleted: "Reel deleted",
    ownReelHint: "These options only work on other people's reels.",
    notInterested: "Not interested",
    block: "Block",
    report: "Report",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    editTitle: "Edit description",
    edited: "Description updated",
    hidden: "You won't see this video again",
    blockTitle: "Block {name}?",
    blockWarning: "You won't see their posts and you'll unfollow them. You can unblock them from their profile.",
    blocked: "User blocked",
    reportTitle: "Why are you reporting this reel?",
    reported: "Report sent. Thanks for letting us know.",
    actionError: "That didn't work. Please try again.",
    reasonSpam: "Spam or scam",
    reasonSexual: "Nudity or sexual content",
    reasonViolence: "Violence or dangerous content",
    reasonHate: "Hate or harassment",
    reasonFalse: "False information",
    reasonChildSafety: "Child safety",
    reasonOther: "Something else",
  },
};

const REPORT_REASONS = [
  { id: "spam", labelKey: "reasonSpam" },
  { id: "sexual", labelKey: "reasonSexual" },
  { id: "violence", labelKey: "reasonViolence" },
  { id: "hate", labelKey: "reasonHate" },
  { id: "false_info", labelKey: "reasonFalse" },
  { id: "child_safety", labelKey: "reasonChildSafety" },
  { id: "other", labelKey: "reasonOther" },
];

const SPONSORED_EVERY = 3;
const SHEET_OPTION_SX = { typography: "body1", fontWeight: 600, gap: 1.5, py: 1.5, borderRadius: "12px" };

const DIALOG_PAPER_SX = {
  bgcolor: "#141414",
  backgroundImage: "none",
  color: "#fff",
  borderRadius: "18px",
};

const getVideoUrl = (post) => {
  const mediaVideo = Array.isArray(post.media)
    ? post.media.find((item) => item?.type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(item?.url || ""))
    : null;
  return mediaVideo?.url || post.videoUrl || "";
};

const ReelAction = ({ icon, count, color = "#fff", onClick }) => (
  <Stack alignItems="center" spacing={0.1}>
    <IconButton onClick={onClick} sx={{ color, p: 0.75 }}>
      {icon}
    </IconButton>
    {count !== undefined && (
      <Typography variant="caption" sx={{ color: "#fff", fontSize: "0.72rem", lineHeight: 1 }}>
        {count}
      </Typography>
    )}
  </Stack>
);

const ReelItem = ({ post, url, author, userId, muted, paused, adId, sponsoredLabel, audienceLabel, onToggleMute, actions }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pausedByUser, setPausedByUser] = useState(false);
  const [playing, setPlaying] = useState(false);
  const edit = useMemo(() => normalizeReelEdit(post), [post]);
  const loopsNatively = edit.trimStart === 0 && !edit.trimEnd;
  const isPublic = isPublicPost(post);
  const safeLink = getSafeLink(post.linkUrl);
  const taggedUsers = Array.isArray(post.taggedUsers) ? post.taggedUsers.filter((person) => person?.uid) : [];

  useEffect(() => {
    if (visible && adId) recordAdEvent(adId, "impression");
  }, [visible, adId]);

  const openLink = () => {
    if (adId) recordAdEvent(adId, "click");
    window.open(safeLink, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.intersectionRatio >= VISIBLE_THRESHOLD),
      { threshold: [0, VISIBLE_THRESHOLD, 1] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (visible && !paused && !pausedByUser) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [visible, paused, pausedByUser]);

  // Al salir de pantalla vuelve a empezar y se olvida la pausa manual.
  useEffect(() => {
    if (visible) return;
    setPausedByUser(false);
    if (videoRef.current) videoRef.current.currentTime = edit.trimStart;
  }, [visible, edit.trimStart]);

  const restartFromTrim = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = edit.trimStart;
    video.play().catch(() => {});
  };

  const hasLiked = post.likes?.includes(userId);
  const hasSaved = post.savedBy?.includes(userId);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", maxWidth: 480, height: "100%", bgcolor: "#000" }}>
        <Box
          component="video"
          ref={videoRef}
          src={url}
          poster={post.coverUrl || undefined}
          muted={muted || edit.muted}
          loop={loopsNatively}
          playsInline
          preload={visible ? "auto" : "metadata"}
          onLoadedMetadata={() => {
            const video = videoRef.current;
            if (!video) return;
            video.playbackRate = edit.speed;
            if (edit.trimStart) video.currentTime = edit.trimStart;
          }}
          onTimeUpdate={() => {
            if (edit.trimEnd && videoRef.current?.currentTime >= edit.trimEnd) restartFromTrim();
          }}
          onEnded={() => {
            if (!loopsNatively) restartFromTrim();
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onClick={() => {
            // Si el navegador bloqueó la reproducción automática, el toque la inicia.
            if (!playing) {
              setPausedByUser(false);
              videoRef.current?.play().catch(() => {});
            } else {
              setPausedByUser(true);
            }
          }}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            cursor: "pointer",
            filter: edit.filterCss,
          }}
        />

        <ReelOverlays overlays={edit.overlays} />

        {!playing && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                bgcolor: "rgba(0,0,0,0.5)",
                borderRadius: "50%",
                width: 64,
                height: 64,
                display: "grid",
                placeItems: "center",
              }}
            >
              <PlayArrowRounded sx={{ color: "#fff", fontSize: 38 }} />
            </Box>
          </Box>
        )}

        {!edit.muted && (
          <IconButton
            onClick={onToggleMute}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
            }}
          >
            {muted ? <VolumeOffRounded fontSize="small" /> : <VolumeUpRounded fontSize="small" />}
          </IconButton>
        )}

        {/* Autor y texto: el degradado los mantiene legibles sobre videos claros */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            pl: 2,
            pr: 8,
            pt: 6,
            pb: 2,
            color: "#fff",
            background: "linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0))",
            pointerEvents: "none",
          }}
        >
          {audienceLabel && (
            <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#00d4ff", fontWeight: 700 }}>
              {audienceLabel}
            </Typography>
          )}
          {adId && (
            <Typography
              variant="caption"
              sx={{
                display: "inline-block",
                mb: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                bgcolor: "rgba(0,212,255,0.3)",
                color: "#00d4ff",
                fontWeight: 700,
              }}
            >
              {sponsoredLabel}
            </Typography>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => {
              if (adId) recordAdEvent(adId, "click");
              actions.onOpenProfile();
            }}
            sx={{ cursor: "pointer", pointerEvents: "auto", width: "fit-content", maxWidth: "100%" }}
          >
            <Avatar src={author.photo} sx={{ width: 34, height: 34 }} />
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
              {author.name}
            </Typography>
            {author.verified && <Verified sx={{ fontSize: 15, color: "#35b6ff", flexShrink: 0 }} />}
          </Stack>
          {post.location && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <PlaceOutlined sx={{ fontSize: 15 }} />
              <Typography variant="caption" noWrap>{String(post.location).slice(0, 80)}</Typography>
            </Stack>
          )}
          {post.text && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {post.text}
            </Typography>
          )}
          {taggedUsers.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 1, mt: 0.5, pointerEvents: "auto" }}>
              {taggedUsers.slice(0, 20).map((person) => (
                <Typography
                  key={person.uid}
                  variant="caption"
                  onClick={() => actions.onOpenUser(person.uid)}
                  sx={{ color: "#00d4ff", fontWeight: 700, cursor: "pointer" }}
                >
                  @{person.username || person.displayName}
                </Typography>
              ))}
            </Stack>
          )}
          {safeLink && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={openLink}
              sx={{ mt: 0.75, width: "fit-content", maxWidth: "100%", cursor: "pointer", pointerEvents: "auto", color: "#00d4ff" }}
            >
              <LinkIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>
                {new URL(safeLink).hostname}
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack spacing={1} alignItems="center" sx={{ position: "absolute", right: 6, bottom: 16 }}>
          <ReelAction
            icon={hasLiked ? <Favorite /> : <FavoriteBorder />}
            color={hasLiked ? "#ff4b4b" : "#fff"}
            count={post.likes?.length || 0}
            onClick={actions.onLike}
          />
          <ReelAction icon={<ChatBubbleOutline />} count={post.commentsCount || 0} onClick={actions.onComment} />
          {/* Republicar o enviar sacaría un reel privado de su público */}
          {isPublic && <ReelAction icon={<Repeat />} count={post.repostCount || 0} onClick={actions.onRepost} />}
          {isPublic && <ReelAction icon={<Send />} count={post.sentCount || 0} onClick={actions.onSend} />}
          <ReelAction
            icon={hasSaved ? <Bookmark /> : <BookmarkBorder />}
            color={hasSaved ? "#00d4ff" : "#fff"}
            onClick={actions.onSave}
          />
          {isPublic && <ReelAction icon={<IosShare />} onClick={actions.onShare} />}
          <ReelAction icon={<MoreVert />} onClick={actions.onMore} />
        </Stack>
      </Box>
    </Box>
  );
};

const Reels = ({
  posts,
  userId,
  language,
  sidebarWidth = 0,
  paused = false,
  sponsoredPostIds = [],
  sponsoredAdIds = {},
  onPromotePost,
  getAuthor,
  onLike,
  onComment,
  onRepost,
  onSend,
  onSave,
  onShare,
  onOpenProfile,
  onEditPost,
  onDeletePost,
  onNotInterested,
  onBlockUser,
  onReportPost,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const t = useTranslatedDictionary(REELS_TEXT, language);
  const [muted, setMuted] = useState(true);
  const orderRef = useRef([]);
  const [menu, setMenu] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [blockingPost, setBlockingPost] = useState(null);
  const [reportingPost, setReportingPost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const overlayOpen = Boolean(menu || editing || deletingPost || blockingPost || reportingPost);
  const isOwnReel = menu?.post.uid === userId;

  const runAction = async (action, successKey, onDone) => {
    setBusy(true);
    try {
      await action();
      onDone?.();
      setToast({ type: "success", text: t(successKey) });
    } catch (error) {
      console.error(error);
      setToast({ type: "error", text: t("actionError") });
    } finally {
      setBusy(false);
    }
  };

  const openFromMenu = (open) => {
    const post = menu?.post;
    setMenu(null);
    if (post) open(post);
  };

  // Las republicaciones se omiten para no repetir el mismo video.
  const videoPosts = useMemo(
    () => posts.filter((post) => !post.isRepost && getVideoUrl(post)),
    [posts],
  );

  // El feed se reordena con cada like: acá el orden se fija al entrar para que el video
  // que se está viendo no salte de lugar. Los reels nuevos se agregan al final.
  const reels = useMemo(() => {
    const byId = new Map(videoPosts.map((post) => [post.id, post]));
    const kept = orderRef.current.filter((id) => byId.has(id));
    const fresh = videoPosts.map((post) => post.id).filter((id) => !kept.includes(id));
    orderRef.current = [...kept, ...fresh];
    const ordered = orderRef.current.map((id) => byId.get(id));

    // Patrocinados: uno después del primer reel y luego cada tres.
    const organic = ordered.filter((post) => !sponsoredPostIds.includes(post.id));
    const sponsored = ordered.filter((post) => sponsoredPostIds.includes(post.id));
    const mixed = [];
    organic.forEach((post, index) => {
      mixed.push(post);
      if (index % SPONSORED_EVERY === 0 && sponsored.length > 0) mixed.push(sponsored.shift());
    });
    return [...mixed, ...sponsored];
  }, [videoPosts, sponsoredPostIds]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: sidebarWidth,
        zIndex: 200,
        bgcolor: "#000",
        color: "#fff",
      }}
    >
      <Box
        sx={{
          height: isDesktop ? "100%" : `calc(100% - ${BOTTOM_NAV_HEIGHT}px)`,
          overflowY: "auto",
          overscrollBehaviorY: "contain",
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {reels.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", px: 3, textAlign: "center" }}>
            <SmartDisplay sx={{ fontSize: 48, color: "rgba(255,255,255,0.45)" }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("emptyTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
              {t("emptyHint")}
            </Typography>
          </Stack>
        ) : (
          reels.map((post) => (
            <ReelItem
              key={post.id}
              post={post}
              url={getVideoUrl(post)}
              author={getAuthor(post)}
              userId={userId}
              muted={muted}
              paused={paused || overlayOpen}
              adId={sponsoredAdIds[post.id] || null}
              sponsoredLabel={t("sponsored")}
              audienceLabel={
                post.audience === "followers"
                  ? t("audienceFollowers")
                  : post.audience === "close_friends"
                    ? t("audienceCloseFriends")
                    : ""
              }
              onToggleMute={() => setMuted((value) => !value)}
              actions={{
                onLike: () => onLike(post.id, post.likes),
                onComment: () => onComment(post.id),
                onRepost: () => onRepost(post),
                onSend: () => onSend(post),
                onSave: () => onSave(post.id, post.savedBy),
                onShare: () => onShare(post),
                onOpenProfile: () => onOpenProfile(post.uid),
                onOpenUser: (uid) => onOpenProfile(uid),
                onMore: () => setMenu({ post }),
              }}
            />
          ))
        )}
      </Box>

      {reels.length > 0 && (
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 12,
            left: isDesktop ? "50%" : 16,
            transform: isDesktop ? "translateX(-224px)" : "none",
            fontWeight: 700,
            textShadow: "0 1px 6px rgba(0,0,0,0.7)",
            pointerEvents: "none",
          }}
        >
          {t("title")}
        </Typography>
      )}

      <SwipeableDrawer
        anchor="bottom"
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        onOpen={() => {}}
        disableSwipeToOpen
        disableDiscovery
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "100%",
            maxWidth: 480,
            mx: "auto",
            px: 1,
            pt: 1,
            pb: "calc(16px + env(safe-area-inset-bottom))",
            bgcolor: "#141414",
            backgroundImage: "none",
            color: "#fff",
            borderRadius: "22px 22px 0 0",
          },
        }}
      >
        <Box sx={{ position: "relative", height: 32, mb: 0.5 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.3)", mx: "auto", mt: 0.5 }} />
          <IconButton
            aria-label={t("close")}
            onClick={() => setMenu(null)}
            size="small"
            sx={{ position: "absolute", top: -2, right: 0, color: "#fff" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        {isOwnReel && (
          <>
            <ListItemButton
              onClick={() => openFromMenu((post) => setEditing({ post, text: post.text || "" }))}
              sx={SHEET_OPTION_SX}
            >
              <Edit />
              {t("edit")}
            </ListItemButton>
            {isPublicPost(menu?.post) && (
              <ListItemButton
                onClick={() => openFromMenu((post) => onPromotePost(post.id))}
                sx={{ ...SHEET_OPTION_SX, color: "#ffd700" }}
              >
                <Campaign />
                {t("promote")}
              </ListItemButton>
            )}
            <ListItemButton onClick={() => openFromMenu(setDeletingPost)} sx={{ ...SHEET_OPTION_SX, color: "#ff6b6b" }}>
              <DeleteOutline />
              {t("delete")}
            </ListItemButton>
          </>
        )}
        {/* Desactivadas en reels propios: bloquearse a uno mismo ocultaría todas sus publicaciones */}
        <ListItemButton
          disabled={busy || isOwnReel}
          onClick={() => openFromMenu((post) => runAction(() => onNotInterested(post.id), "hidden"))}
          sx={SHEET_OPTION_SX}
        >
          <VisibilityOff />
          {t("notInterested")}
        </ListItemButton>
        <ListItemButton
          disabled={isOwnReel}
          onClick={() => openFromMenu(setBlockingPost)}
          sx={{ ...SHEET_OPTION_SX, color: "#ff6b6b" }}
        >
          <Block />
          {t("block")}
        </ListItemButton>
        <ListItemButton
          disabled={isOwnReel}
          onClick={() => openFromMenu(setReportingPost)}
          sx={{ ...SHEET_OPTION_SX, color: "#ff6b6b" }}
        >
          <Flag />
          {t("report")}
        </ListItemButton>
        {isOwnReel && (
          <Typography variant="caption" sx={{ display: "block", px: 2, pt: 0.5, color: "rgba(255,255,255,0.55)" }}>
            {t("ownReelHint")}
          </Typography>
        )}
      </SwipeableDrawer>

      <Dialog
        open={Boolean(editing)}
        onClose={() => !busy && setEditing(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("editTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            value={editing?.text || ""}
            onChange={(e) => setEditing((prev) => ({ ...prev, text: e.target.value }))}
            InputProps={{ disableUnderline: true, sx: { color: "#fff" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setEditing(null)} sx={{ color: "#fff", textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button
            disabled={busy}
            onClick={() => runAction(() => onEditPost(editing.post.id, editing.text), "edited", () => setEditing(null))}
            sx={{ color: "#00d4ff", textTransform: "none", fontWeight: 700 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingPost)}
        onClose={() => !busy && setDeletingPost(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            {t("deleteWarning")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDeletingPost(null)} sx={{ color: "#fff", textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button
            disabled={busy}
            onClick={() => runAction(() => onDeletePost(deletingPost.id), "deleted", () => setDeletingPost(null))}
            sx={{ color: "#ff6b6b", textTransform: "none", fontWeight: 700 }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(blockingPost)}
        onClose={() => !busy && setBlockingPost(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("blockTitle").replace("{name}", blockingPost ? getAuthor(blockingPost).name : "")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            {t("blockWarning")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setBlockingPost(null)} sx={{ color: "#fff", textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button
            disabled={busy}
            onClick={() => runAction(() => onBlockUser(blockingPost.uid), "blocked", () => setBlockingPost(null))}
            sx={{ color: "#ff6b6b", textTransform: "none", fontWeight: 700 }}
          >
            {t("block")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reportingPost)}
        onClose={() => !busy && setReportingPost(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("reportTitle")}</DialogTitle>
        <DialogContent sx={{ px: 1.5 }}>
          <Stack>
            {REPORT_REASONS.map((reason) => (
              <Button
                key={reason.id}
                disabled={busy}
                onClick={() =>
                  runAction(() => onReportPost(reportingPost, reason.id), "reported", () => setReportingPost(null))
                }
                sx={{ color: "#fff", textTransform: "none", justifyContent: "flex-start", py: 1.1 }}
              >
                {t(reason.labelKey)}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setReportingPost(null)} sx={{ color: "#fff", textTransform: "none" }}>
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast?.type || "success"} variant="filled" onClose={() => setToast(null)} sx={{ borderRadius: "12px" }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reels;
